import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import type { Company, CompanyInsert, CompanyUpdate } from '../../types/supabase'
import { kaseyaAPI } from '../../services/kaseya-api'

// Get current user's company
export const useCompany = () => {
  return useQuery({
    queryKey: ['company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', user.user_metadata?.company_id)
        .single()

      if (error) throw error
      return data as Company
    },
    enabled: true
  })
}

export interface CompanySummary extends Company {
  user_count: number
  device_count: number
  ticket_count: number
}

// Get company by id (admin view)
export const useCompanyById = (companyId?: string) => {
  return useQuery({
    queryKey: ['companyById', companyId],
    queryFn: async () => {
      if (!companyId) throw new Error('companyId is required')

      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .maybeSingle()

      if (error) throw error
      return data as Company | null
    },
    enabled: !!companyId
  })
}

// Get all companies (admin view)
export const useCompaniesList = () => {
  return useQuery({
    queryKey: ['companiesList'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('companies')
        .select(`
        id,
        name,
        slug,
        logo_url,
        subscription_tier,
        contact_phone,
        contact_address,
        stripe_customer_id,
        kaseya_organization_id,
        kaseya_group_id,
        created_at,
        updated_at,
        users:users(count),
        devices:devices(count),
          tickets:tickets(count)
        `)
        .order('created_at', { ascending: false })

      if (error) throw error

      return (data || []).map((company: any) => ({
        id: company.id,
        name: company.name,
        slug: company.slug,
        logo_url: company.logo_url,
        subscription_tier: company.subscription_tier,
        stripe_customer_id: company.stripe_customer_id,
        contact_phone: company.contact_phone,
        contact_address: company.contact_address,
        kaseya_organization_id: company.kaseya_organization_id,
        kaseya_group_id: company.kaseya_group_id,
        created_at: company.created_at,
        updated_at: company.updated_at,
        user_count: company.users?.[0]?.count ?? 0,
        device_count: company.devices?.[0]?.count ?? 0,
        ticket_count: company.tickets?.[0]?.count ?? 0,
      })) as CompanySummary[]
    },
    enabled: true
  })
}

// Get all companies (simple list for dropdowns)
export const useAllCompanies = () => {
  return useQuery({
    queryKey: ['companies', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, kaseya_organization_id, kaseya_group_id, default_kaseya_group_id')
        .order('name')

      if (error) throw error
      return data as Company[]
    }
  })
}

// Create a new company
export const useCreateCompany = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (companyData: CompanyInsert) => {
      // Step 1: Create company in local database first
      const { data: company, error } = await supabase
        .from('companies')
        .insert(companyData)
        .select()
        .single()

      if (error) throw error

      console.log(`✅ Created company locally: ${company.name} (ID: ${company.id})`)

      // Step 2: Create organization in Kaseya (bidirectional sync)
      try {
        const kaseyaOrg = await kaseyaAPI.createOrganization(company.name)

        console.log(`✅ Created organization in Kaseya: ${kaseyaOrg.Name} (ID: ${kaseyaOrg.Id})`)

        // Step 3: Create a default site under the organization
        const kaseyaSite = await kaseyaAPI.createSite({
          Name: `${company.name} - Main Site`,
          ParentId: kaseyaOrg.Id
        })

        console.log(`✅ Created site in Kaseya: ${kaseyaSite.Name} (ID: ${kaseyaSite.Id})`)

        // Step 4: Create a default machine group under the site
        const kaseyaGroup = await kaseyaAPI.createGroup({
          Name: `${company.name} - Default Group`,
          ParentId: kaseyaSite.Id,
          Notes: 'Automatically created by Helpdesk 411'
        })

        console.log(`✅ Created machine group in Kaseya: ${kaseyaGroup.Name} (ID: ${kaseyaGroup.Id})`)

        // Step 5: Update local company with Kaseya IDs
        const { data: updatedCompany, error: updateError } = await supabase
          .from('companies')
          .update({
            kaseya_organization_id: kaseyaOrg.Id,
            kaseya_group_id: kaseyaGroup.Id
          })
          .eq('id', company.id)
          .select()
          .single()

        if (updateError) {
          console.error('Failed to update company with Kaseya IDs:', updateError)
          throw updateError
        }

        console.log(`✅ Updated local company with Kaseya IDs: Org ${kaseyaOrg.Id}, Group ${kaseyaGroup.Id}`)

        return updatedCompany as Company
      } catch (kaseyaError) {
        console.error('❌ Failed to create organization in Kaseya:', kaseyaError)
        // Return the local company even if Kaseya sync fails
        // This prevents blocking company creation if Kaseya is unavailable
        console.warn('⚠️  Company created locally but not synced to Kaseya')
        return company as Company
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company'] })
      queryClient.invalidateQueries({ queryKey: ['companiesList'] })
    }
  })
}

// Update company
export const useUpdateCompany = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updateData }: { id: string } & CompanyUpdate) => {
      // Step 1: Get current company data to check for Kaseya mapping
      const { data: currentCompany, error: fetchError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', id)
        .single()

      if (fetchError) throw fetchError

      // Step 2: Update local database
      const { data: updatedCompany, error } = await supabase
        .from('companies')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      console.log(`✅ Updated company locally: ${updatedCompany.name}`)

      // Step 3: Sync name changes to Kaseya (if mapped and name changed)
      if (currentCompany.kaseya_organization_id && updateData.name && updateData.name !== currentCompany.name) {
        try {
          await kaseyaAPI.updateOrganization(currentCompany.kaseya_organization_id, updateData.name)
          console.log(`✅ Synced company name to Kaseya organization ${currentCompany.kaseya_organization_id}`)
        } catch (kaseyaError) {
          console.error('❌ Failed to sync name to Kaseya:', kaseyaError)
          console.warn('⚠️  Company updated locally but name not synced to Kaseya')
          // Don't throw - allow local update to succeed even if Kaseya sync fails
        }
      }

      return updatedCompany as Company
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company'] })
      queryClient.invalidateQueries({ queryKey: ['companiesList'] })
      queryClient.invalidateQueries({ queryKey: ['companyById'] })
    }
  })
}

// Delete company (admin only)
// NOTE: Kaseya VSA API v3 does not support DELETE operations for organizations/groups
// This only deletes from local database - Kaseya records must be deleted manually if needed
export const useDeleteCompany = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (companyId: string) => {
      // Delete from local database only
      // Kaseya API doesn't support DELETE operations (returns 405 Method Not Allowed)
      const { error: deleteError } = await supabase
        .from('companies')
        .delete()
        .eq('id', companyId)

      if (deleteError) throw deleteError

      console.log(`✅ Deleted company from local database (Kaseya record remains if exists)`)
      return companyId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companiesList'] })
    }
  })
}
